import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent,
  CircularProgress, Alert, Divider, Chip, Link,
} from '@mui/material';
import {
  PeopleOutlined, FolderOutlined, SearchOutlined,
  BugReportOutlined, AutoFixHighOutlined, SecurityOutlined,
  CodeOutlined, AccessTimeOutlined, CallMergeOutlined, EnergySavingsLeaf,
} from '@mui/icons-material';
import type { RootState } from '../../store/store';
import { useGetAdminStatsQuery } from '../../store/api/adminApi';

const ADMIN_EMAIL = 'mmohammad@leangains.ai';

function formatLoc(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatUsd(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, sub, color = '#1976d2' }) => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 36, height: 36, borderRadius: 1.5, bgcolor: `${color}18`, color, flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
      </Box>
      <Typography variant="h4" fontWeight={800} sx={{ color, lineHeight: 1 }}>{value}</Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const AdminPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const isAdmin = (user?.email ?? '').toLowerCase() === ADMIN_EMAIL.toLowerCase();
  const { data, isLoading, error } = useGetAdminStatsQuery(undefined, { skip: !isAdmin });

  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading stats…</Typography>
      </Box>
    );
  }

  if (error || !data) {
    return <Alert severity="error">Failed to load admin stats.</Alert>;
  }

  return (
    <Box sx={{ p: 4, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography variant="h4" fontWeight={800}>KodeGauge Analytics</Typography>
            <Chip label="Internal" size="small" color="warning" sx={{ fontWeight: 700 }} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Aggregate metrics across all customers — for marketing, sales, and investor reporting.
          </Typography>
        </Box>
        <Link component={RouterLink} to="/admin/languages" sx={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>
          Language & Tooling Coverage →
        </Link>
      </Box>

      {/* ── Reach ─────────────────────────────────────────────────────────── */}
      <Typography variant="overline" color="text.disabled" fontWeight={700}>Reach</Typography>
      <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PeopleOutlined fontSize="small" />}
            label="Total users"
            value={data.users.total}
            sub={`${data.users.pro} Pro · ${data.users.free} Free`}
            color="#7c3aed"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<FolderOutlined fontSize="small" />}
            label="Repos connected"
            value={data.repos.total}
            color="#0891b2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<SearchOutlined fontSize="small" />}
            label="Scans run"
            value={data.scans.total.toLocaleString()}
            color="#0891b2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CodeOutlined fontSize="small" />}
            label="Lines of code scanned"
            value={formatLoc(data.scans.loc_scanned)}
            sub={`${data.scans.loc_scanned.toLocaleString()} total`}
            color="#0891b2"
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* ── Issues found ──────────────────────────────────────────────────── */}
      <Typography variant="overline" color="text.disabled" fontWeight={700}>Issues found</Typography>
      <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<BugReportOutlined fontSize="small" />}
            label="Total issues found"
            value={data.issues.total.toLocaleString()}
            color="#dc2626"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<BugReportOutlined fontSize="small" />}
            label="Critical"
            value={(data.issues.by_severity['critical'] ?? 0).toLocaleString()}
            color="#dc2626"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<BugReportOutlined fontSize="small" />}
            label="High"
            value={(data.issues.by_severity['high'] ?? 0).toLocaleString()}
            color="#ea580c"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<SecurityOutlined fontSize="small" />}
            label="Security issues"
            value={(data.issues.by_category['security'] ?? 0).toLocaleString()}
            color="#dc2626"
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* ── Value delivered ───────────────────────────────────────────────── */}
      <Typography variant="overline" color="text.disabled" fontWeight={700}>Value delivered</Typography>

      {/* Hero total */}
      <Card elevation={0} sx={{
        border: '2px solid', borderColor: '#16a34a', borderRadius: 2,
        bgcolor: '#f0fdf4', mt: 0.5, mb: 2, px: 3, py: 2,
        display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap',
      }}>
        <Box>
          <Typography variant="h3" fontWeight={900} sx={{ color: '#16a34a', lineHeight: 1 }}>
            {formatUsd(data.fixes.total_value_delivered)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Total value delivered to customers
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#0891b2' }}>
            {formatUsd(data.fixes.dev_cost_saved)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Developer time saved · {data.fixes.dev_hours_saved.toLocaleString()} hrs @ $75/hr
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#dc2626' }}>
            {formatUsd(data.fixes.security_risk_eliminated)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Security risk eliminated · based on per-severity breach-cost model
          </Typography>
        </Box>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<AutoFixHighOutlined fontSize="small" />}
            label="Fixes merged to production"
            value={data.fixes.merged.toLocaleString()}
            color="#16a34a"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<SecurityOutlined fontSize="small" />}
            label="Critical vulnerabilities eliminated"
            value={data.fixes.critical_eliminated.toLocaleString()}
            color="#dc2626"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<SecurityOutlined fontSize="small" />}
            label="Security issues fixed"
            value={data.fixes.security_eliminated.toLocaleString()}
            color="#ea580c"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<AccessTimeOutlined fontSize="small" />}
            label="Developer hours saved"
            value={data.fixes.dev_hours_saved.toLocaleString()}
            sub="trivial 0.5h · simple 2h · moderate 8h · complex 24h"
            color="#16a34a"
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* ── PRs & Refactors ───────────────────────────────────────────────── */}
      <Typography variant="overline" color="text.disabled" fontWeight={700}>PRs & Refactors</Typography>
      <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CallMergeOutlined fontSize="small" />}
            label="Fix PRs raised"
            value={data.fixes.prs_raised.toLocaleString()}
            color="#7c3aed"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CallMergeOutlined fontSize="small" />}
            label="Refactor PRs raised"
            value={data.refactor.prs_raised.toLocaleString()}
            color="#7c3aed"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CallMergeOutlined fontSize="small" />}
            label="Refactor PRs merged"
            value={data.refactor.prs_merged.toLocaleString()}
            color="#16a34a"
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* ── Carbon impact ─────────────────────────────────────────────────── */}
      <Typography variant="overline" color="text.disabled" fontWeight={700}>Carbon & Energy Impact</Typography>

      {/* Hero carbon banner */}
      <Card elevation={0} sx={{
        border: '2px solid #86efac', borderRadius: 2,
        bgcolor: '#f0fdf4', mt: 0.5, mb: 2, px: 3, py: 2,
        display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap',
      }}>
        <EnergySavingsLeaf sx={{ fontSize: 40, color: '#16a34a', flexShrink: 0 }} />
        <Box>
          <Typography variant="h3" fontWeight={900} sx={{ color: '#16a34a', lineHeight: 1 }}>
            {data.carbon.co2g_saved_from_merged_fixes >= 1000
              ? `${(data.carbon.co2g_saved_from_merged_fixes / 1000).toFixed(2)} kg`
              : `${Math.round(data.carbon.co2g_saved_from_merged_fixes)} g`} CO₂
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Estimated CO₂ saved from merged fixes across all customers
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#0891b2' }}>
            {data.carbon.kwh_saved_from_merged_fixes.toFixed(2)} kWh
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Energy saved per month · based on Green Software Foundation SCI
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#15803d' }}>
            {data.carbon.co2g_saved_from_merged_fixes >= 1000
              ? `${(data.carbon.co2g_saved_from_merged_fixes / 1000 * 12).toFixed(1)} kg`
              : `${Math.round(data.carbon.co2g_saved_from_merged_fixes * 12)} g`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Projected annual CO₂ saving at current fix rate
          </Typography>
        </Box>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<EnergySavingsLeaf fontSize="small" />}
            label="CO₂ saved (merged fixes)"
            value={data.carbon.co2g_saved_from_merged_fixes >= 1000
              ? `${(data.carbon.co2g_saved_from_merged_fixes / 1000).toFixed(2)} kg`
              : `${Math.round(data.carbon.co2g_saved_from_merged_fixes)} g`}
            sub="grams CO₂ equivalent per month"
            color="#16a34a"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<EnergySavingsLeaf fontSize="small" />}
            label="Energy saved (merged fixes)"
            value={`${data.carbon.kwh_saved_from_merged_fixes.toFixed(2)} kWh`}
            sub="kilowatt-hours per month"
            color="#0891b2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<EnergySavingsLeaf fontSize="small" />}
            label="Projected annual saving"
            value={data.carbon.co2g_saved_from_merged_fixes >= 1000
              ? `${(data.carbon.co2g_saved_from_merged_fixes / 1000 * 12).toFixed(1)} kg CO₂`
              : `${Math.round(data.carbon.co2g_saved_from_merged_fixes * 12)} g CO₂`}
            sub="extrapolated at current monthly rate"
            color="#15803d"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminPage;
